// Cave84 — Cellars hooks (Phase 0 Day 4)
// TanStack Query wrappers around services/cellars.ts. Pages and components
// consume these hooks; they never import from services/cellars.ts directly.
//
// Caching policy:
//   - List query is shared via qk.cellars.all and stays fresh for the
//     QueryClient default (2 min, see main.tsx).
//   - Mutations invalidate the list and any per-id query.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import type { Cellar } from '@cave84/shared-types';
import {
  createCellar,
  deleteCellarRaw,
  getCellar,
  listCellars,
  updateCellar,
  type CellarCreateInput,
  type CellarUpdateInput
} from '@/services/cellars';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';

export function useCellars(): UseQueryResult<Cellar[]> {
  return useQuery({
    queryKey: qk.cellars.all,
    queryFn: listCellars
  });
}

export function useCellar(id: string | undefined): UseQueryResult<Cellar | null> {
  return useQuery({
    queryKey: id ? qk.cellars.byId(id) : qk.cellars.byId('__noop__'),
    queryFn: () => (id ? getCellar(id) : Promise.resolve(null)),
    enabled: !!id
  });
}

// ---------------------------------------------------------------------------
// Mutations — owner only. We assert the role at call time so non-owner UI
// paths can't accidentally trigger a mutation that the rules will reject
// with a confusing permission-denied error.
// ---------------------------------------------------------------------------

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') {
    throw new Error('Cave84: this mutation requires the owner role.');
  }
}

export function useCreateCellar(): UseMutationResult<Cellar, Error, CellarCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CellarCreateInput) => {
      assertOwner();
      return createCellar(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
    }
  });
}

export function useUpdateCellar(): UseMutationResult<
  void,
  Error,
  { id: string; patch: CellarUpdateInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      assertOwner();
      await updateCellar(id, patch);
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
      void qc.invalidateQueries({ queryKey: qk.cellars.byId(id) });
    }
  });
}

export function useDeleteCellar(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      assertOwner();
      // F8: callers must verify the cellar is empty first. The rules layer
      // will reject the delete if bottles still reference this cellar, but
      // that produces a permission-denied error rather than a clean message.
      await deleteCellarRaw(id);
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
      void qc.removeQueries({ queryKey: qk.cellars.byId(id) });
    }
  });
}
